import { useBuildRun } from '../state/buildRun';
import { Button, Glass } from '../ui';

/**
 * A build is a server job: POST /builds, poll every 3s, then finalizeBuild writes the
 * project and the router lands on the editor. This renders from store state and is
 * mounted globally, so navigating away does not cancel the build — the same design the
 * phone uses.
 */
const PHASES: Record<string, string> = {
  queued: 'Getting ready…',
  running: 'Building your app…',
  planning: 'Working out the plan…',
  generating: 'Writing your app…',
  enriching: 'Adding the finishing touches…',
  seeding: 'Filling it with example content…',
  done: 'Almost there…',
};

/** An unrecognised phase still says something rather than leaving the panel blank. */
function phaseLabel(phase: string): string {
  return PHASES[phase] ?? `${phase.replace(/[_-]+/g, ' ')}…`;
}

export default function BuildOverlay() {
  const status = useBuildRun((s) => s.status);
  const phase = useBuildRun((s) => s.phase);
  const error = useBuildRun((s) => s.error);
  const outOfCredits = useBuildRun((s) => s.outOfCredits);

  if (status === 'idle') return null;

  return (
    <div className="build-overlay">
      <Glass className="build-panel">
        {status === 'error' ? (
          <>
            <h2 className="type-title">That didn&rsquo;t work</h2>
            <p role="alert" className="type-body build-message">{error}</p>
            <div className="build-actions">
              {/* Refused for credits, "Try again" is refused again, every time: the way forward
                  is credits. Every other failure keeps Try again. */}
              {outOfCredits ? (
                <Button title="Get credits" onPress={() => useBuildRun.getState().getCredits()} />
              ) : (
                <Button title="Try again" onPress={() => useBuildRun.getState().retry()} />
              )}
              <Button title="Close" variant="ghost" onPress={() => useBuildRun.getState().dismiss()} />
            </div>
          </>
        ) : (
          <>
            <span className="build-spinner" aria-hidden="true" />
            <p role="status" aria-live="polite" className="type-title build-message">
              {phaseLabel(phase)}
            </p>
            <p className="type-body build-sub">You can keep browsing — this carries on.</p>
          </>
        )}
      </Glass>
    </div>
  );
}
